import {LayerVO, LegendItemVO, ServicesApi} from '../api';
import {LayerId} from '@mc/react-mapio';
import {getWmsWmtsLayerIds} from './LayerConfigUtils';

/** Data potřebná pro vykreslení modálního okna legendy, přednačtená před jeho otevřením. */
export type PreloadedLegendData = {
    /** Popisky atributů, podle kterých lze ve vrstvách fulltextově vyhledávat (klíč = ID vrstvy). */
    searchableAttributes: {[key: string]: string[]};
    /** Konfigurace všech vrstev. */
    layersConfig: {[key: string]: LayerVO};
    /** Legendy WMS/WMTS vrstev z capabilities (klíč = ID vrstvy). */
    layerLegends: {[key: string]: LegendItemVO};
};

/**
 * Přednačte všechna data potřebná pro modální okno legendy (atributy pro vyhledávání, konfiguraci
 * vrstev a legendy WMS/WMTS vrstev) jedním paralelním {@link Promise.all}, aby se okno otevřelo
 * rovnou s daty bez interních spinnerů.
 *
 * Seznam WMS/WMTS vrstev (pro které se legenda načítá z capabilities) se určí z již načtené
 * konfigurace vrstev v komponentě – díky tomu mohou všechny tři dotazy běžet paralelně.
 *
 * @param layerIds - ID vrstev, pro které se legenda zobrazuje
 * @param lang - jazyk (pro konfiguraci vrstev)
 * @param context - kontext aplikace (pro konfiguraci vrstev)
 * @param layersConfig - již načtená konfigurace vrstev v komponentě (pro určení WMS/WMTS vrstev)
 */
export const preloadLegendData = async (
    layerIds: LayerId[],
    lang: string,
    context: string,
    layersConfig: {[key: string]: LayerVO} | undefined,
): Promise<PreloadedLegendData> => {
    const wmsWmtsLayerIds = getWmsWmtsLayerIds(layersConfig, layerIds);
    const [searchableAttributes, layersConfigData, layerLegends] = await Promise.all([
        ServicesApi.getSearchableAttributes(layerIds.map(String)).then(response => response.data),
        ServicesApi.getLayersConfig(lang, context).then(response => response.data),
        wmsWmtsLayerIds.length > 0
            ? ServicesApi.getLayerLegends(wmsWmtsLayerIds).then(response => response.data)
            : Promise.resolve<{[key: string]: LegendItemVO}>({}),
    ]);

    return {searchableAttributes, layersConfig: layersConfigData, layerLegends};
};
